import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import { Send, MessageSquare } from "lucide-react";

export default function Messages() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const endRef = useRef(null);

  const loadConvos = useCallback(async () => {
    const res = await api.get("/conversations");
    setConvos(res.data);
    const to = params.get("to");
    if (to) {
      const existing = res.data.find((c) => c.user?.user_id === to);
      if (existing) setActive(existing.user);
      else {
        const u = await api.get(`/users/${to}`);
        setActive({ user_id: u.data.user_id, name: u.data.name, picture: u.data.picture });
      }
    } else if (res.data.length && !active) {
      setActive(res.data[0].user);
    }
  }, [params]);

  useEffect(() => { loadConvos(); }, [loadConvos]);

  const loadThread = useCallback(async () => {
    if (!active?.user_id) return;
    const res = await api.get(`/messages/${active.user_id}`);
    setMessages(res.data);
  }, [active]);

  useEffect(() => { loadThread(); }, [loadThread]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!input.trim() || !active) return;
    const content = input.trim();
    setInput("");
    setMessages((m) => [...m, { message_id: "tmp" + Date.now(), sender_id: user.user_id, content, created_at: new Date().toISOString() }]);
    await api.post("/messages", { receiver_id: active.user_id, content });
    loadConvos();
  };

  return (
    <div>
      <h1 className="font-display font-bold text-4xl uppercase tracking-tight text-white mb-6">Messages</h1>
      <div className="grid md:grid-cols-3 gap-4 h-[calc(100vh-220px)]">
        {/* Convo list */}
        <div className="bg-[#1E1E1E] border border-[#2A2A2A] rounded-2xl overflow-y-auto no-scrollbar">
          {convos.length === 0 && <p className="text-sm text-[#A0A0A0] p-4">No conversations yet. Message a coach from Discover!</p>}
          {convos.map((c) => (
            <button key={c.user?.user_id} data-testid={`convo-${c.user?.user_id}`} onClick={() => setActive(c.user)} className={`w-full flex items-center gap-3 p-4 border-b border-[#2A2A2A] text-left transition-colors ${active?.user_id === c.user?.user_id ? "bg-[#2A2A2A]" : "hover:bg-[#2A2A2A]/50"}`}>
              <img src={c.user?.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">{c.user?.name}</p>
                <p className="text-xs text-[#A0A0A0] truncate">{c.last_message}</p>
              </div>
            </button>
          ))}
        </div>

        {/* Thread */}
        <div className="md:col-span-2 bg-[#1E1E1E] border border-[#2A2A2A] rounded-2xl flex flex-col">
          {active ? (
            <>
              <div className="p-4 border-b border-[#2A2A2A] flex items-center gap-3">
                <img src={active.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
                <p className="font-semibold text-white">{active.name}</p>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2 no-scrollbar">
                {messages.map((m) => (
                  <div key={m.message_id} className={`flex ${m.sender_id === user.user_id ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${m.sender_id === user.user_id ? "bg-[#FF6B6B] text-black" : "bg-[#2A2A2A] text-white"}`}>{m.content}</div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <div className="p-3 border-t border-[#2A2A2A] flex gap-2">
                <input data-testid="message-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message…" className="flex-1 bg-[#121212] border border-[#2A2A2A] rounded-full px-4 py-2.5 text-sm text-white placeholder-[#A0A0A0] focus:outline-none focus:ring-1 focus:ring-[#FF6B6B]" />
                <button data-testid="send-message-btn" onClick={send} className="w-10 h-10 rounded-full bg-[#FF6B6B] flex items-center justify-center hover:brightness-110 transition-all"><Send className="w-4 h-4 text-black" /></button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-[#A0A0A0]">
              <MessageSquare className="w-10 h-10 mb-2" />
              <p className="text-sm">Select a conversation</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
